import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Notifications.css";
import Bellicon from "../icon/bellicon";
import { resolveAvatarUrl, handleAvatarError } from "../../utils/profilePicUrl";
import { getApiBaseUrl } from "../../config/api";

const API_URL = getApiBaseUrl() || process.env.REACT_APP_API_URL;

/** Parse API datetimes; naive MySQL values are Asia/Singapore (+08:00). */
function parseServerDate(dateString) {
  if (!dateString) return new Date(NaN);
  const s = String(dateString).trim();
  if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)) {
    return new Date(s);
  }
  const normalized = s.includes("T") ? s : s.replace(" ", "T");
  return new Date(`${normalized}+08:00`);
}

// 🕒 Format timestamps as “2s ago”, “3m ago”, “1 day ago”, etc.
function timeAgo(dateString) {
  const seconds = Math.floor((Date.now() - parseServerDate(dateString).getTime()) / 1000);

  if (Number.isNaN(seconds) || seconds < 0) return "just now";
  if (seconds < 60) return seconds < 5 ? "just now" : `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(days / 365);
  return `${years}y ago`;
}

/** Turn absolute frontend URLs into in-app paths when possible. */
function toAppPath(link) {
  if (!link) return null;
  try {
    if (link.startsWith("/")) return link;
    const url = new URL(link);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return link;
  }
}

export default function Notifications({ userId }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [showList, setShowList] = useState(false);
  const [popupNotif, setPopupNotif] = useState(null);

  const fetchNotifications = async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API_URL}/get_notifications.php?user_id=${userId}`);
      const raw = await res.text();
      let data = null;
      try {
        data = JSON.parse(raw);
      } catch {
        console.error("Notifications endpoint returned non-JSON:", raw);
        return;
      }
      if (data.success) {
        if (notifications.length && data.notifications.length > notifications.length) {
          const latest = data.notifications[0];
          showPopup(latest);
        }
        setNotifications(data.notifications);
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5000);
    return () => clearInterval(interval);
  }, [userId]);

  const unreadCount = notifications.filter(
    (n) => n.is_read === "0" || n.is_read === 0
  ).length;

  const openLink = (link) => {
    const path = toAppPath(link);
    if (!path) return;
    if (path.startsWith("/")) {
      navigate(path);
      setShowList(false);
    } else {
      window.open(link, "_blank");
    }
  };

  const handleNotificationClick = async (notif) => {
    // Always navigate — even if the notification was already read
    openLink(notif.link);

    const alreadyRead = notif.is_read === 1 || notif.is_read === "1";
    if (alreadyRead) return;

    // Optimistically mark as read in the UI
    setNotifications((prev) =>
      prev.map((n) => (n.id === notif.id ? { ...n, is_read: 1 } : n))
    );

    try {
      await fetch(`${API_URL}/mark_read.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: notif.id }),
      });
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  const showPopup = (notif) => {
    setPopupNotif(notif);
    setTimeout(() => setPopupNotif(null), 3000);
  };

  return (
    <div className="notif-wrapper">
      <button className="notif-btn" onClick={() => setShowList(!showList)}>
        <Bellicon />
        {unreadCount > 0 && <span className="notif-count">{unreadCount}</span>}
      </button>

      {showList && (
        <div className="notif-dropdown">
          <div className="notif-header">Notifications</div>
          {notifications.length === 0 ? (
            <p className="notif-empty">No notifications yet</p>
          ) : (
            notifications.map((n) => {
              const senderName = n.username !== "Unknown" ? n.username : "Someone";
              const msg = (n.message || "").replace(senderName, "").trim();

              return (
                <div
                  key={n.id}
                  className={`notif-item ${
                    n.is_read === 0 || n.is_read === "0" ? "unread" : ""
                  }`}
                  onClick={() => handleNotificationClick(n)}
                >
                  <img
                    src={resolveAvatarUrl(n, API_URL)}
                    alt="Profile"
                    className="notif-avatar"
                    onError={handleAvatarError}
                  />
                  <div className="notif-content">
                    <span className="notif-message">
                      <strong>{senderName}</strong> {msg || n.message}
                    </span>
                    <span className="notif-time">{timeAgo(n.created_at)}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {popupNotif && (
        <div
          className="notif-popup"
          onClick={() => {
            handleNotificationClick(popupNotif);
            setPopupNotif(null);
          }}
        >
          <strong>
            {popupNotif.username !== "Unknown" ? popupNotif.username : "Someone"}
          </strong>{" "}
          {(popupNotif.message || "").replace(
            popupNotif.username !== "Unknown" ? popupNotif.username : "Someone",
            ""
          )}
        </div>
      )}
    </div>
  );
}
